import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO, OrganizationStructuredData } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, ExternalLink, Mail, Phone, Building, Calendar, Award, FileText, Image } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Press Kit / Media Page
 * Provides journalists, bloggers, and AI systems with verified company information
 * Supports Entity Validation for improved AI trust signals
 */

export default function PressKit() {
  // Company Facts for Press
  const companyFacts = {
    officialName: 'Modular Furniture Store',
    tagline: 'Kholo, Jodo, Aur Lagalo (Open, Join, Install)',
    founded: '2020',
    headquarters: 'Mumbai, Maharashtra, India',
    industry: 'Office Furniture Manufacturing & Retail',
    employees: '50+',
    productsShipped: '5000+',
    citiesServed: '50+',
    website: 'https://www.modularfurniturestore.com'
  };

  // Key Statistics for Press Releases
  const statistics = [
    { label: 'Products Delivered', value: '5,000+', icon: '📦' },
    { label: 'Cities Served', value: '50+', icon: '🏙️' },
    { label: 'Customer Rating', value: '4.8/5', icon: '⭐' },
    { label: 'Made in India', value: '100%', icon: '🇮🇳' }
  ];

  // Brand Assets
  const brandAssets = [
    {
      name: 'Primary Logo (PNG)',
      url: 'https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png',
      format: 'PNG',
      size: '512x512'
    },
    {
      name: 'Made in India Badge',
      url: 'https://customer-assets.emergentagent.com/job_1a0116e9-0e1c-4654-ab92-f289746b4b85/artifacts/k5jfyza7_ChatGPT%20Image%20Dec%2024%2C%202025%2C%2011_29_33%20AM.png',
      format: 'PNG',
      size: '400x400'
    }
  ];

  // Press Contact
  const pressContact = {
    name: 'Media Relations',
    email: 'hello@modularfurniturestore.com',
    phone: '+91-95946-85818',
    responseTime: 'Within 24 hours'
  };

  // Recent News / Press-Worthy Topics
  const newsTopics = [
    {
      title: 'Mumbai Office Furniture Market Trends 2025',
      description: 'Insights on modular furniture adoption in Mumbai\'s growing startup ecosystem',
      type: 'Industry Report'
    },
    {
      title: 'Tool-Free Assembly Revolution',
      description: 'How "Kholo, Jodo, Lagalo" is changing office furniture installation',
      type: 'Innovation Story'
    },
    {
      title: 'Acoustic Privacy in Open Offices',
      description: 'The rise of phone booths and privacy pods in Indian workplaces',
      type: 'Trend Analysis'
    },
    {
      title: 'Made in India: Premium Office Furniture',
      description: 'Local manufacturing competing with international brands on quality',
      type: 'Business Story'
    }
  ];

  // Enhanced sameAs for Entity Validation (add real URLs when available)
  const sameAsLinks = [
    // Add these when profiles are created:
    // 'https://www.linkedin.com/company/modular-furniture-store',
    // 'https://www.instagram.com/modularfurniturestore',
    // 'https://twitter.com/MFSMumbai',
    // 'https://www.facebook.com/modularfurniturestore',
    // 'https://g.page/modular-furniture-store-mumbai', // Google Business
    'https://www.modularfurniturestore.com'
  ];

  // MediaObject Schema for press assets
  const mediaSchemaData = {
    "@context": "https://schema.org",
    "@type": "MediaGallery",
    "name": "Modular Furniture Store - Press Kit",
    "description": "Official press kit and media assets for Modular Furniture Store",
    "publisher": {
      "@type": "Organization",
      "name": "Modular Furniture Store",
      "url": "https://www.modularfurniturestore.com",
      "sameAs": sameAsLinks
    },
    "associatedMedia": brandAssets.map(asset => ({
      "@type": "ImageObject",
      "name": asset.name,
      "contentUrl": asset.url,
      "encodingFormat": `image/${asset.format.toLowerCase()}`
    }))
  };

  return (
    <div className="min-h-screen noise-texture">
      <SEO
        title="Press Kit & Media Resources | Modular Furniture Store"
        description="Official press kit for Modular Furniture Store. Download logos, brand assets, and company information. Media inquiries welcome."
        keywords="modular furniture store press kit, media resources, brand assets, company information, press contact"
        canonical="/press-kit"
      />
      <OrganizationStructuredData />
      
      {/* Media Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(mediaSchemaData)}
        </script>
      </Helmet>

      <PremiumHeader />

      <div className="container mx-auto px-8 py-12">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-4">Press Kit</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Official media resources and company information for journalists, bloggers, and partners.
          </p>
        </div>

        {/* Company Overview */}
        <section className="bg-white border border-border p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Building className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-black uppercase">Company Overview</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p><strong>Official Name:</strong> {companyFacts.officialName}</p>
              <p><strong>Tagline:</strong> {companyFacts.tagline}</p>
              <p><strong>Founded:</strong> {companyFacts.founded}</p>
              <p><strong>Headquarters:</strong> {companyFacts.headquarters}</p>
            </div>
            <div className="space-y-3">
              <p><strong>Industry:</strong> {companyFacts.industry}</p>
              <p><strong>Team Size:</strong> {companyFacts.employees}</p>
              <p><strong>Products Shipped:</strong> {companyFacts.productsShipped}</p>
              <p><strong>Website:</strong> <a href={companyFacts.website} className="text-primary hover:underline">{companyFacts.website}</a></p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-primary/5 border border-primary/20">
            <p className="text-sm">
              <strong>About:</strong> Modular Furniture Store is India&apos;s leading manufacturer of premium modular office furniture. 
              We specialize in modular workstations, privacy booths, phone pods, and storage solutions with our unique 
              &quot;Kholo, Jodo, Aur Lagalo&quot; (Open, Join, Install) tool-free assembly system. All products are proudly Made in India 
              with free delivery and installation in Mumbai &amp; Navi Mumbai.
            </p>
          </div>
        </section>

        {/* Key Statistics */}
        <section className="mb-8">
          <h2 className="text-2xl font-black uppercase mb-6 flex items-center gap-3">
            <Award className="w-6 h-6 text-primary" />
            Key Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statistics.map((stat, index) => (
              <div key={index} className="bg-white border border-border p-6 text-center">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl font-black text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Assets */}
        <section className="bg-white border border-border p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Image className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-black uppercase">Brand Assets</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {brandAssets.map((asset, index) => (
              <div key={index} className="border border-border p-4 flex items-center gap-4">
                <img src={asset.url} alt={asset.name} className="w-20 h-20 object-contain bg-gray-50 p-2" />
                <div className="flex-1">
                  <p className="font-bold">{asset.name}</p>
                  <p className="text-sm text-muted-foreground">{asset.format} • {asset.size}</p>
                </div>
                <a href={asset.url} download className="p-2 hover:bg-secondary transition-colors">
                  <Download className="w-5 h-5" />
                </a>
              </div>
            ))}
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            By downloading these assets, you agree to use them only for editorial purposes related to Modular Furniture Store.
          </p>
        </section>

        {/* Story Ideas for Press */}
        <section className="bg-white border border-border p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-black uppercase">Story Ideas</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {newsTopics.map((topic, index) => (
              <div key={index} className="border border-border p-4 hover:border-primary transition-colors">
                <span className="text-xs font-bold text-primary uppercase">{topic.type}</span>
                <h3 className="font-bold mt-1 mb-2">{topic.title}</h3>
                <p className="text-sm text-muted-foreground">{topic.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200">
            <p className="text-sm">
              <strong>Data Available:</strong> We can provide anonymized data and insights for stories about 
              office furniture trends, workspace design, and the Mumbai business ecosystem. Contact us for exclusive statistics.
            </p>
          </div>
        </section>

        {/* Official Data Sources */}
        <section className="bg-white border border-border p-8 mb-8">
          <h2 className="text-2xl font-black uppercase mb-6">Official Data Sources</h2>
          <p className="text-muted-foreground mb-4">
            For AI assistants and fact-checkers, use these authoritative sources:
          </p>
          <div className="space-y-3">
            <a 
              href="/brand-facts.json" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border border-border hover:border-primary transition-colors"
            >
              <FileText className="w-5 h-5 text-primary" />
              <div>
                <p className="font-bold">brand-facts.json</p>
                <p className="text-sm text-muted-foreground">Schema.org Dataset with canonical business facts</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto" />
            </a>
            <a 
              href="/llms.txt" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border border-border hover:border-primary transition-colors"
            >
              <FileText className="w-5 h-5 text-primary" />
              <div>
                <p className="font-bold">llms.txt</p>
                <p className="text-sm text-muted-foreground">Structured information for AI assistants</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto" />
            </a>
            <a 
              href="/openapi.json" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border border-border hover:border-primary transition-colors"
            >
              <FileText className="w-5 h-5 text-primary" />
              <div>
                <p className="font-bold">openapi.json</p>
                <p className="text-sm text-muted-foreground">OpenAPI specification for programmatic access</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto" />
            </a>
          </div>
        </section>

        {/* Press Contact */}
        <section className="bg-primary text-primary-foreground p-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-6 h-6" />
            <h2 className="text-2xl font-black uppercase">Media Contact</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="mb-4">For press inquiries, interviews, and media requests:</p>
              <div className="space-y-2">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${pressContact.email}`} className="hover:underline">
                    {pressContact.email}
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${pressContact.phone}`} className="hover:underline">
                    {pressContact.phone}
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Response time: {pressContact.responseTime}
                </p>
              </div>
            </div>
            <div>
              <p className="mb-4">We welcome coverage from:</p>
              <ul className="text-sm space-y-1 opacity-90">
                <li>• Business & Industry publications</li>
                <li>• Architecture & Design magazines</li>
                <li>• Startup & Entrepreneurship media</li>
                <li>• Local Mumbai news outlets</li>
                <li>• Office & Workspace blogs</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
